import { useCallback, useRef, useState } from "react";
import Webcam from "react-webcam";
import styled from "styled-components";

import { dataUrlToFile } from "@utils/forms/dataUrlToFile";
import { Button, color, Feedback, font } from "@runwayhealth/runway-components-react";

export type CameraCaptureProps = {
  onCapture: (file: File) => void;
  onCancel: () => void;
};

type CameraStatus = "loading" | "live" | "preview" | "error";

const CAPTURED_PHOTO_FILENAME = "camera-capture.jpg";

// Fixed, literal copy — never the native DOMException's .message/.name, which
// can read as raw browser-internal text like "NotAllowedError: Permission denied".
const CAMERA_ERROR_MESSAGE = "We couldn't access your camera. You can upload a photo of your ID instead.";

// `ideal`, not `exact`, so this degrades gracefully on single-camera devices
// instead of throwing OverconstrainedError. Defaults to the back/environment
// camera, correct for photographing a physical ID card.
const videoConstraints: MediaStreamConstraints["video"] = {
  facingMode: { ideal: "environment" },
};

// Captured ID photos are rendered as a data-URL <img>; PostHog session replay
// serializes DOM attributes, so the whole component must be blocked from capture.
const Container = styled.div.attrs({
  className: "ph-no-capture",
})`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const VideoWrapper = styled.div`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: ${color.brand.secondary.dark};
  aspect-ratio: 4 / 3;

  video {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const LoadingOverlay = styled.p`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  color: white;
  font-family: ${font.family};
`;

const FrameGuide = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  aspect-ratio: 1.586 / 1;
  border: 2px dashed rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  pointer-events: none;
`;

const FrameGuideCopy = styled.p`
  position: absolute;
  bottom: 12px;
  left: 0;
  right: 0;
  margin: 0;
  text-align: center;
  color: white;
  font-family: ${font.family};
  font-size: 0.75rem;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const CameraCapture = ({ onCapture, onCancel }: CameraCaptureProps) => {
  const [status, setStatus] = useState<CameraStatus>("loading");
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const webcamRef = useRef<Webcam>(null);

  // onUserMediaError alone only covers the *initial* getUserMedia() failure —
  // a patient revoking camera permission via browser chrome after the live
  // preview is already showing needs its own signal, so each track's own
  // "ended" event is routed into the same error fallback.
  const handleUserMedia = useCallback((stream: MediaStream) => {
    setStatus("live");
    stream.getTracks().forEach((track) => {
      track.addEventListener("ended", () => setStatus("error"), { once: true });
    });
  }, []);

  const handleUserMediaError = useCallback(() => {
    setStatus("error");
  }, []);

  const handleCapture = () => {
    const screenshot = webcamRef.current?.getScreenshot();
    if (!screenshot) return;
    setCapturedImage(screenshot);
    setStatus("preview");
  };

  const handleRetake = () => {
    setCapturedImage(null);
    setStatus("loading");
  };

  const handleConfirm = () => {
    if (!capturedImage) return;
    // A live photo of a government ID must never reach console/Sentry/telemetry,
    // even on a conversion failure — surface only a generic, static fallback.
    try {
      const file = dataUrlToFile(capturedImage, CAPTURED_PHOTO_FILENAME);
      onCapture(file);
    } catch {
      setStatus("error");
    }
  };

  if (status === "error") {
    return (
      <Container>
        <Feedback isInvalid>{CAMERA_ERROR_MESSAGE}</Feedback>
        <Controls>
          <Button size="sm" autoFocus onClick={onCancel}>
            Upload a file instead
          </Button>
        </Controls>
      </Container>
    );
  }

  if (status === "preview" && capturedImage) {
    return (
      <Container>
        <VideoWrapper>
          <img src={capturedImage} alt="Captured photo of your ID" />
        </VideoWrapper>
        <Controls>
          <Button size="sm" variant="outline" onClick={handleRetake}>
            Retake
          </Button>
          <Button size="sm" autoFocus onClick={handleConfirm}>
            Use this photo
          </Button>
        </Controls>
      </Container>
    );
  }

  return (
    <Container>
      <VideoWrapper>
        <Webcam
          ref={webcamRef}
          audio={false}
          screenshotFormat="image/jpeg"
          videoConstraints={videoConstraints}
          onUserMedia={handleUserMedia}
          onUserMediaError={handleUserMediaError}
        />
        {status === "loading" && <LoadingOverlay role="status">Starting camera…</LoadingOverlay>}
        {status === "live" && (
          <>
            <FrameGuide />
            <FrameGuideCopy>Position your ID within the frame</FrameGuideCopy>
          </>
        )}
      </VideoWrapper>
      <Controls>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Use a file instead
        </Button>
        <Button size="sm" onClick={handleCapture} disabled={status !== "live"}>
          Capture
        </Button>
      </Controls>
    </Container>
  );
};

export default CameraCapture;
